import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, ChevronDown } from 'lucide-react';
import logo from '../assets/logo.png';

export default function Header() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();

    const darkGreen = "#1A2E20";
    const goldAccent = "#B89561";

    const portalUrl = "";

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setActiveDropdown(null);
        setMobileMenuOpen(false);
    }, [location]);

    if (location.pathname === '/login') return null;

    const navLinks = [
        { name: 'Home', path: '/home' },
        { name: 'About Us', path: '/Aboutus' },
        { name: 'Our Community', path: '/OurCommunity' },
        {
            name: (
                <span className="text-left leading-tight inline-block">
                    D.Manike Ranathunga <br/>
                    <span className="text-[7px] opacity-60 block tracking-widest uppercase">Memorial Library</span>
                </span>
            ),
            path: '/library'
        },
        {
            name: 'Projects',
            path: '#',
            id: 'projects',
            dropdown: [
                { name: 'Annual & Ongoing', path: '/projects/annual' },
                { name: 'Completed Projects', path: '/projects/completed' }
            ]
        },
        {
            name: 'Media',
            path: '#',
            id: 'media',
            dropdown: [
                { name: 'Gallery', path: '/gallery' },
                { name: 'Blog', path: '/blog' },
                { name: 'E-Magazine', path: '/emagazine' }
            ]
        },
        { name: 'Contact', path: '/contact' },
    ];

    const textColor = isScrolled ? 'text-[#1A2E20]' : 'text-white';

    // Active Check helper function
    const isTabActive = (link) => {
        if (link.dropdown) {
            return link.dropdown.some(subItem => location.pathname === subItem.path);
        }
        return location.pathname === link.path;
    };

    return (
        <nav className={`fixed w-full z-50 transition-all duration-500 px-8 ${
            isScrolled
                ? 'bg-white/95 backdrop-blur-lg shadow-md py-4'
                : 'bg-gradient-to-b from-black/50 to-transparent py-7'
        }`}>
            <div className="max-w-7xl mx-auto flex justify-between items-center">

                {/* LOGO SECTION */}
                <div className="flex items-center cursor-pointer group flex-shrink-0" onClick={() => navigate('/home')}>
                    <div className="relative flex items-center">
                        {!isScrolled && <div className="absolute inset-0 bg-white/10 blur-xl rounded-full scale-150" />}
                        <img src={logo} alt="Boseth Logo" className="w-10 h-10 md:w-12 md:h-12 object-contain transition-all duration-500 relative z-10" />
                    </div>
                    <div className="flex flex-col ml-3 justify-center">
                        <span className={`font-serif text-2xl md:text-3xl leading-tight uppercase transition-colors duration-500 ${textColor}`}>Boseth</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <span style={{ color: isScrolled ? darkGreen : goldAccent }} className="text-[7px] md:text-[8px] tracking-[0.3em] uppercase font-bold transition-colors duration-500 whitespace-nowrap">Buddhist</span>
                            <span style={{ color: isScrolled ? darkGreen : goldAccent }} className="text-[7px] md:text-[8px] tracking-[0.3em] opacity-80 uppercase font-medium transition-colors duration-500 whitespace-nowrap">Sanctuary</span>
                        </div>
                    </div>
                </div>

                {/* DESKTOP NAV */}
                <div className="hidden lg:flex items-center ml-auto gap-5 xl:gap-8">
                    {navLinks.map((link, index) => {
                        const active = isTabActive(link);
                        return (
                            <div
                                key={index}
                                className="relative group"
                                onMouseEnter={() => link.dropdown && setActiveDropdown(link.id)}
                                onMouseLeave={() => link.dropdown && setActiveDropdown(null)}
                            >
                                {link.dropdown ? (
                                    <button
                                        style={{ '--hover-color': goldAccent, color: active ? goldAccent : undefined }}
                                        className={`flex items-center gap-1 relative text-[10px] xl:text-[11px] uppercase tracking-[0.2em] xl:tracking-[0.25em] font-bold transition-all duration-300 py-2 ${textColor} hover:text-[var(--hover-color)]`}
                                    >
                                        {link.name}
                                        <ChevronDown size={12} className={`transition-transform duration-300 ${activeDropdown === link.id ? 'rotate-180' : ''}`} />
                                        <span
                                            style={{ backgroundColor: goldAccent }}
                                            className={`absolute bottom-0 h-[2px] transition-all duration-300 group-hover:w-full group-hover:left-0 ${active ? 'w-full left-0' : 'w-0 left-1/2'}`}
                                        />
                                    </button>
                                ) : (
                                    <Link
                                        to={link.path}
                                        style={{ '--hover-color': goldAccent, color: active ? goldAccent : undefined }}
                                        className={`flex items-center gap-1 relative text-[10px] xl:text-[11px] uppercase tracking-[0.2em] xl:tracking-[0.25em] font-bold transition-all duration-300 py-2 ${textColor} hover:text-[var(--hover-color)]`}
                                    >
                                        {link.name}
                                        <span
                                            style={{ backgroundColor: goldAccent }}
                                            className={`absolute bottom-0 h-[2px] transition-all duration-300 group-hover:w-full group-hover:left-0 ${active ? 'w-full left-0' : 'w-0 left-1/2'}`}
                                        />
                                    </Link>
                                )}

                                {/* DROPDOWN MENU */}
                                {link.dropdown && (
                                    <AnimatePresence>
                                        {activeDropdown === link.id && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 10 }}
                                                className="absolute top-full right-0 lg:left-0 w-64 bg-white shadow-xl rounded-lg py-4 border-t-4 border-[#1A2E20] overflow-hidden"
                                            >
                                                {link.dropdown.map((sub, i) => {
                                                    const isSubActive = location.pathname === sub.path;
                                                    return (
                                                        <Link
                                                            key={i}
                                                            to={sub.path}
                                                            style={{ color: isSubActive ? goldAccent : darkGreen }}
                                                            className="block px-6 py-3 text-[11px] uppercase tracking-widest hover:bg-gray-50 hover:pl-8 transition-all font-bold"
                                                        >
                                                            {sub.name}
                                                        </Link>
                                                    );
                                                })}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                )}
                            </div>
                        );
                    })}

                    {/* PORTAL BUTTON */}
                    <a
                        // href={portalUrl}
                        style={{
                            backgroundColor: isScrolled ? darkGreen : 'transparent',
                            borderColor: isScrolled ? darkGreen : 'rgba(255,255,255,0.4)'
                        }}
                        className={`flex items-center gap-2 px-5 py-2.5 border rounded-full transition-all duration-500 hover:scale-105 group shadow-md shadow-black/5 ml-4 ${
                            isScrolled ? 'text-white' : 'text-white bg-white/5 backdrop-blur-sm'
                        }`}
                    >
                        <span className="text-[10px] uppercase tracking-widest font-bold">Portal</span>
                        <User size={14} className="group-hover:rotate-12 transition-transform" />
                    </a>
                </div>

                {/* MOBILE UI TOGGLE */}
                <div className="lg:hidden flex items-center">
                    <button className={`${textColor} p-2`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                        {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            {/* MOBILE MENU */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: '100%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: '100%' }}
                        className="fixed inset-0 h-screen bg-[#FDFBF7] flex flex-col items-center justify-center p-8 z-[60] lg:hidden overflow-y-auto"
                    >
                        <button onClick={() => setMobileMenuOpen(false)} className="absolute top-8 right-8 text-[#1A2E20]">
                            <X size={32} />
                        </button>

                        <div className="flex flex-col items-center gap-4 w-full">
                            {navLinks.map((link, index) => {
                                const active = isTabActive(link);
                                return (
                                    <div key={index} className="flex flex-col items-center w-full">
                                        {!link.dropdown ? (
                                            <Link
                                                to={link.path}
                                                style={{ color: active ? goldAccent : '#1A2E20' }}
                                                className="uppercase text-xl md:text-2xl tracking-[0.1em] font-serif py-2 text-center"
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                {link.name}
                                            </Link>
                                        ) : (
                                            <div className="flex flex-col items-center gap-2 my-2">
                                                <span className="text-[#1A2E20] opacity-40 uppercase text-[10px] tracking-widest font-bold">{link.name}</span>
                                                {link.dropdown.map((sub, i) => {
                                                    const isSubActive = location.pathname === sub.path;
                                                    return (
                                                        <Link
                                                            key={i}
                                                            to={sub.path}
                                                            style={{ color: isSubActive ? goldAccent : '#1A2E20' }}
                                                            className="uppercase text-lg tracking-[0.1em] font-serif py-1 text-center"
                                                            onClick={() => setMobileMenuOpen(false)}
                                                        >
                                                            {sub.name}
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {/* MOBILE PORTAL BUTTON */}
                            <a
                                href={portalUrl}
                                onClick={() => setMobileMenuOpen(false)}
                                style={{ backgroundColor: darkGreen }}
                                className="mt-8 px-10 py-4 text-white rounded-full uppercase tracking-[0.2em] font-bold text-sm shadow-lg text-center"
                            >
                                Access Portal
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
}